import type { GameWorld } from '../world/GameWorld';
import type { GameAssets } from '../../game/assets/GameAssets';
import { validateTerrainRenderAssets } from '../../game/terrain/TerrainAssetValidator';
import { EntityRenderer } from './EntityRenderer';
import { BuildingActivityRenderer } from './BuildingActivityRenderer';
import type { LayerManager } from './LayerManager';
import { SeasonEffectsRenderer } from './SeasonEffectsRenderer';
import { TilemapRenderer } from './TilemapRenderer';
import { RealmBrandingRenderer } from './RealmBrandingRenderer';
import { DYNAMIC_WORLD_RULES } from './DynamicWorldRules';
import type { RuntimeBranding } from '../../game/runtimeConfig';

interface RendererOptions {
  readonly visualPolish?: boolean;
  readonly showAllLabels?: boolean;
  readonly waterMode?: 'tiles';
  readonly branding?: RuntimeBranding;
}

export class Renderer {
  private readonly tilemapRenderer: TilemapRenderer;
  private readonly entityRenderer: EntityRenderer;
  private readonly buildingActivityRenderer: BuildingActivityRenderer;
  private readonly seasonEffectsRenderer: SeasonEffectsRenderer;
  private readonly realmBrandingRenderer: RealmBrandingRenderer;

  constructor(layers: LayerManager, assets: GameAssets, options: RendererOptions = {}) {
    const terrainAssets = validateTerrainRenderAssets(assets);
    if (terrainAssets.missing.length > 0) {
      console.warn('[Stream Kingdoms] Fehlende Terrain-Assets:', terrainAssets.missing);
    }
    this.tilemapRenderer = new TilemapRenderer(layers.tileLayer, assets, options);
    this.entityRenderer = new EntityRenderer(layers.entityLayer, assets, options);
    this.realmBrandingRenderer = new RealmBrandingRenderer(
      layers.entityLayer,
      options.branding ?? {
        primaryColor: '#d6a84f',
        secondaryColor: '#4f8fd6',
        logoUrl: '',
        showLogoOnBanners: false,
      },
    );
    this.buildingActivityRenderer = new BuildingActivityRenderer(layers.effectsLayer);
    this.seasonEffectsRenderer = new SeasonEffectsRenderer(layers.seasonGroundLayer, layers.effectsLayer);
    layers.backgroundLayer.visible = DYNAMIC_WORLD_RULES.stageBackdrops;
    layers.tileLayer.visible = true;
  }

  render(world: GameWorld): void {
    const snapshot = world.getSnapshot();
    this.seasonEffectsRenderer.render(snapshot.map, snapshot.season);
    this.buildingActivityRenderer.render(snapshot);
    this.tilemapRenderer.render(snapshot.map, snapshot.season);
    this.entityRenderer.render([...snapshot.decorations, ...snapshot.buildings, ...snapshot.units], snapshot.season);
    this.realmBrandingRenderer.render(snapshot);
  }

  getCastleState(): null {
    return null;
  }
}
